import type { FC } from "react"
import { useState } from "react"

import * as Headless from "@headlessui/react"

import { useCreateConcertSellerUser } from "~/api/hooks/useCreateConcertSellerUser"

type CreateConcertUserModalProps = {
  userId: string
  name: string
  email: string
  phone: string | null
  license: string | null
  onClose: () => void
}

const isValidUsPhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, ``)
  const normalized =
    digits.startsWith(`1`) && digits.length > 10 ? digits.slice(1) : digits
  return normalized.length === 10
}

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === `object` && `response` in error) {
    const { response } = error
    if (response && typeof response === `object` && `data` in response) {
      const { data } = response
      if (data && typeof data === `object` && `message` in data) {
        const { message } = data
        if (typeof message === `string` && message.length > 0) {
          return message
        }
        if (Array.isArray(message) && message.length > 0) {
          return message.join(`; `)
        }
      }
    }
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return `Failed to create Concert user. Please try again.`
}

export const CreateConcertUserModal: FC<CreateConcertUserModalProps> = ({
  userId,
  name,
  email,
  phone,
  license,
  onClose,
}) => {
  const createUser = useCreateConcertSellerUser()
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const missingLicense = !license
  const invalidPhone = !phone || !isValidUsPhone(phone)
  const cannotCreate = missingLicense || invalidPhone

  const handleCreate = (): void => {
    if (cannotCreate) {
      return
    }
    setError(null)
    createUser.mutate(
      { userId },
      {
        onSuccess: () => {
          setShowSuccess(true)
        },
        onError: (err: unknown) => {
          setError(getErrorMessage(err))
        },
      },
    )
  }

  const handleClose = (): void => {
    if (!createUser.isLoading) {
      onClose()
    }
  }

  return (
    <Headless.Dialog open onClose={handleClose} className="relative z-[1500]">
      <Headless.DialogBackdrop className="fixed inset-0 bg-foreground/40" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Headless.DialogPanel className="w-full max-w-md rounded-xl bg-background shadow-2xl">
          <div className="px-6 pt-5 pb-2">
            <Headless.DialogTitle className="font-display text-lg font-bold leading-tight text-primary">
              Create Concert User Account
            </Headless.DialogTitle>
            <Headless.Description className="mt-0.5 text-sm text-muted">
              Confirm the details below to create a Concert account
            </Headless.Description>
          </div>

          <div className="px-6 py-5">
            {showSuccess ? (
              <div className="rounded bg-success/10 p-4 text-sm text-foreground">
                <p className="font-semibold">Account created successfully</p>
                <p className="mt-1">
                  The Concert user account has been created. The account status
                  will be refreshed automatically.
                </p>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 rounded bg-danger/10 p-3 text-sm text-foreground">
                    {error}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted">
                      Name
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-foreground">
                      {name}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted">
                      Email
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-foreground">
                      {email}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted">
                      Phone
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-foreground">
                      {phone || `None`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted">
                      License (CA CSLB)
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-foreground">
                      {license || `None`}
                    </p>
                  </div>
                </div>

                {invalidPhone && (
                  <div className="mt-4 rounded bg-warning/10 p-3 text-sm text-foreground">
                    A valid 10-digit US phone number is required before a Concert
                    user account can be created. Please update the phone number
                    in Powur first.
                  </div>
                )}

                {missingLicense && (
                  <div className="mt-4 rounded bg-warning/10 p-3 text-sm text-foreground">
                    A valid CA CSLB license is required before a Concert user
                    account can be created. Please add a license in Powur first.
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 rounded-b-xl border-t border-border bg-surface px-6 py-4">
            {showSuccess ? (
              <button
                onClick={onClose}
                type="button"
                className="cursor-pointer rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:opacity-90"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  type="button"
                  disabled={createUser.isLoading}
                  className="cursor-pointer rounded-md border border-border bg-background px-4 py-2 text-sm text-foreground transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  type="button"
                  disabled={createUser.isLoading || cannotCreate}
                  className="flex cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {createUser.isLoading && (
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  )}
                  {createUser.isLoading ? `Creating...` : `Create Account`}
                </button>
              </>
            )}
          </div>
        </Headless.DialogPanel>
      </div>
    </Headless.Dialog>
  )
}
